import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, AlertCircle, CheckCircle } from 'lucide-react';

export function Login() {
  const navigate = useNavigate();
  const { signInWithMagicLink, authUser, isAdmin, authNotice } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (authUser) {
      if (isAdmin) {
        navigate('/admin/dashboard', { replace: true });
      } else {
        navigate('/dashboard');
      }
    }
  }, [authUser, isAdmin, navigate]);

  const sendMagicLink = useCallback(async (emailToSend: string) => {
    if (!emailToSend) return;

    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      await signInWithMagicLink(emailToSend);
      setSuccess(true);
      setEmail('');
    } catch (err: any) {
      setError(err.message || 'Er ging iets mis bij het inloggen');
    } finally {
      setLoading(false);
    }
  }, [signInWithMagicLink]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendMagicLink(email);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-4 mb-6">
            <img
              src="https://cdn.prod.website-files.com/68106eab8acafbe1be69f17a/6830b7212ae8f51f0b5e545b_12ebc92fd52362293d48e2636ef26ed3_logo.svg"
              alt="Dibbi"
              className="h-12"
            />
            <img
              src="https://cdn.prod.website-files.com/68106eab8acafbe1be69f17a/697a293834ed484d86c80d4b_dibbi.png"
              alt="Dibbi Avatar"
              className="w-12 h-12 rounded-full"
            />
          </div>
          <h1 className="text-3xl font-bold text-[#231F20] mb-2">
            Welkom terug
          </h1>
          <p className="text-gray-600">
            Vul je email in om een inloglink te ontvangen
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          {success ? (
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-[#231F20] mb-2">
                Check je inbox
              </h2>
              <p className="text-gray-600 mb-6">
                We hebben een inloglink gestuurd naar je email. Klik op de link om in te loggen.
              </p>
              <button
                onClick={() => setSuccess(false)}
                className="text-sm text-[#FF6B35] hover:text-[#E55A2B] font-medium transition-colors"
              >
                Opnieuw versturen
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {authNotice && !error && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">{authNotice}</p>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none transition-all"
                    placeholder="je@email.nl"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !email}
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Versturen...
                  </>
                ) : (
                  <>
                    <Mail className="w-5 h-5" />
                    Verstuur inloglink
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          Door in te loggen ga je akkoord met onze{' '}
          <a href="https://www.dibbi.nl/documents/v1.1-algemene-voorwaarden-dibbi.pdf" className="underline hover:text-gray-700" target="_blank" rel="noopener noreferrer">
            voorwaarden
          </a>
        </p>
      </div>
    </div>
  );
}
